<script setup lang="ts">
import { computed } from 'vue'
import { Users, ArrowRight } from 'lucide-vue-next'
import { EntityIcon, gradeBand, type PaletteHue } from '@/design-system/components'

/**
 * ClassCard (§04) — overview grid card: class identity + a three-cell stat
 * strip (avg / to-grade / next) + optional flag chip. 4px class-solid left bar;
 * hover lifts with a class-solid ring.
 */
const props = defineProps<{
  hue: PaletteHue
  name: string
  studentCount: number
  subject?: string | null
  metaLine?: string | null
  avg: number | null
  toGrade: number | null
  nextLabel?: string | null
  flag?: { tone: 'warning' | 'danger'; label: string } | null
}>()
defineEmits<{ open: [] }>()

const avgBand = computed(() => gradeBand(props.avg))
</script>

<template>
  <button
    type="button"
    class="ccard"
    :style="{ '--class-solid': hue.solid, '--class-ink': hue.ink }"
    @click="$emit('open')"
  >
    <span class="ccard__bar" />

    <div class="ccard__identity">
      <EntityIcon :icon="Users" :palette="hue" :size="42" :corner-radius="12" />
      <div class="ccard__id">
        <div class="ccard__titlerow">
          <span class="ccard__name">{{ name }}</span>
          <span v-if="subject" class="ccard__subject">{{ subject }}</span>
        </div>
        <div class="ccard__meta">{{ metaLine || `${studentCount} students` }}</div>
      </div>
      <ArrowRight :size="17" class="ccard__arrow" />
    </div>

    <div class="ccard__strip">
      <div class="ccard__cell">
        <div class="ccard__stat" :style="{ color: avg == null ? 'var(--ink-300)' : avgBand.ink }">
          <template v-if="avg == null">—</template>
          <template v-else>{{ avg }}<span class="ccard__pct">%</span></template>
        </div>
        <div class="ccard__statlabel">Class avg</div>
      </div>
      <div class="ccard__cell">
        <div
          class="ccard__stat"
          :style="{ color: toGrade && toGrade > 0 ? 'var(--coral)' : 'var(--ink-300)' }"
        >
          {{ toGrade && toGrade > 0 ? toGrade : '—' }}
        </div>
        <div class="ccard__statlabel">To grade</div>
      </div>
      <div class="ccard__cell">
        <div class="ccard__stat ccard__stat--next">{{ nextLabel || '—' }}</div>
        <div class="ccard__statlabel">Next</div>
      </div>
    </div>

    <div v-if="flag" class="ccard__flag" :class="`ccard__flag--${flag.tone}`">
      <span class="ccard__flag-dot" />
      {{ flag.label }}
    </div>
  </button>
</template>

<style scoped>
.ccard {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 0 0 4px;
  border: 0;
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--hairline-border);
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  font-family: var(--font-sans);
  transition: box-shadow var(--motion-fast), transform var(--motion-fast);
}
.ccard:hover {
  box-shadow: inset 0 0 0 1px var(--class-solid), 0 6px 20px rgba(15, 15, 20, 0.08);
  transform: translateY(-1px);
}
.ccard__bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--class-solid);
}
.ccard__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 14px;
}
.ccard__id {
  flex: 1;
  min-width: 0;
}
.ccard__titlerow {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ccard__name {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ccard__subject {
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: var(--radius-sm);
  background: var(--palette-indigo-soft);
  color: var(--palette-indigo-ink);
}
.ccard__meta {
  margin-top: 3px;
  font-size: 12.5px;
  color: var(--ink-500);
}
.ccard__arrow {
  flex-shrink: 0;
  color: var(--ink-300);
  transition: color var(--motion-fast);
}
.ccard:hover .ccard__arrow {
  color: var(--class-ink);
}
.ccard__strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid var(--hairline);
}
.ccard__cell {
  padding: 12px 16px;
}
.ccard__cell + .ccard__cell {
  border-left: 1px solid var(--hairline);
}
.ccard__stat {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.4px;
  color: var(--text-primary);
}
.ccard__stat--next {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ccard__pct {
  opacity: 0.55;
  font-weight: 500;
}
.ccard__statlabel {
  margin-top: 2px;
  font-size: 11px;
  color: var(--ink-500);
}
.ccard__flag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 16px 14px;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}
.ccard__flag--warning {
  background: var(--warning-soft);
  color: var(--warning-ink);
}
.ccard__flag--danger {
  background: var(--danger-soft);
  color: var(--danger-ink);
}
.ccard__flag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
</style>
